import { CUSTOM_ELEMENTS_SCHEMA, Injector, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { ExportPreviewDialogComponent } from './shared/export-preview-dialog/export-preview-dialog.component';
import { NumberDirective } from './core/services/numbers-only.directive'
import { GlobalsService } from './core/services/globals.service';
import { SharedModule } from './shared/shared.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import 'hammerjs';
import "@lottiefiles/lottie-player";
import { LoginComponent } from './common/authentication/login/login.component';
import { EngagementLandingPageComponent } from './engagement/components/engagement-landing-page/engagement-landing-page.component';
import { HappinessLandingPageComponent } from './happiness/components/happiness-landing-page/happiness-landing-page.component';
import { TenantSetupComponent } from './common/tenant-setup/tenant-setup.component';
import { BidiModule } from '@angular/cdk/bidi';
import { MainFrameComponent } from './common/main-frame/main-frame.component';
import { LanguagePipe } from './shared/pipes/language.pipe';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, filter } from 'rxjs/operators';
import AbpProvidersModule from './core/services/abp/providers';
import { SnackBarService } from './core/services/snack-bar.service';
import { ExtractPipe } from './shared/pipes/extract.pipe';
import { BandLevelResolver, EntityGroupResolver, ExtraCriteriaResolver, FixedGroupingOptionsResolver, JobGradeResolver, JobRoleResolver, KEY_RESOLVER_TOKEN, OnboardingExperienceScaleResolver, OnboardingPhaseResolver, SurveyStatusResolver } from './onboarding/services/key-resolver.service';
import { MatTreeModule } from '@angular/material/tree';
import { ExcelUploadModule } from './excel-upload/excel-upload.module';
import { AuthTokenInterceptor } from './auth-token.interceptor';
import { TokenService } from './core/services/token.service';
import { MetaService } from './onboarding/services/meta.service';
import { NavigationEnd, Router } from '@angular/router';
import { WellbeingLandingPageComponent } from './wellbeing/features/wellbeing-landing-page/wellbeing-landing-page.component';
import { CultureLandingPageComponent } from './culture/features/culture-landing-page/culture-landing-page.component';
import { AiReadinessLandingPageComponent } from './ai-readiness/features/ai-readiness-landing-page/ai-readiness-landing-page.component';
import { AssessmentLandingPageComponent } from './assessment/components/assessment-landing-page/assessment-landing-page.component';
import { EntityStructureDialogComponent } from './shared/components/entity-structure-dialog/entity-structure-dialog.component';
import { AnnouncementsModule } from './core/modules/announcements/announcements.module';
import { AccountOverviewComponent } from './common/account-overview/account-overview.component';
import { ZenSnackbarComponent } from './shared/Theme/components/zen-snackbar/zen-snackbar.component';
import { ComponentPortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ChangePasswordComponent } from './common/change-password/change-password.component';
import { ResetPasswordComponent } from './common/authentication/reset-password/reset-password.component';
import { ChangeEmailComponent } from './common/change-email/change-email.component';
import { CoreKeyResolverModule } from './core/modules/core-resolver/core-resolver.module';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getDatabase, provideDatabase } from '@angular/fire/database';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { environment } from 'src/environments/environment';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { provideUrls } from './provide-urls';
import { CacheInterceptor } from './core/interceptors/cache.interceptor';
import { StorageService } from './core/services/storage.service';
import { ThemeModule } from './shared/Theme/theme.module';
import { SurveyxLandingComponent } from './survey-x/features/surveyx-landing/surveyx-landing.component';
import registerOpenAPI from './survey-x/register-openapi';


export let AppInjector: Injector;

/** Allows tests (and AppModule) to wire the global injector used by legacy AppInjector.get() call sites. */
export function setAppInjector(injector: Injector): void {
  AppInjector = injector;
}

class GlobalErrorHandler implements HttpInterceptor {

  constructor(private injector: Injector) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    var snackbar = this.injector.get(SnackBarService);

    return next.handle(req).pipe(catchError((err: HttpErrorResponse) => {
      // snackbar.openSnackBar(err.error?.message || err.error?.error?.message, err.statusText)
      throw err;
    }))
  }
}

const tooltipDefaults: MatTooltipDefaultOptions = {
  position: 'above',          // ← global default (top)
  showDelay: 0,
  hideDelay: 0,
  touchendHideDelay: 1500,
  tooltipClass: 'mat-tooltip'
};
@NgModule({
  declarations: [
    AppComponent,
    ExportPreviewDialogComponent,
    NumberDirective,
    LoginComponent,
    EngagementLandingPageComponent,
    HappinessLandingPageComponent,
    WellbeingLandingPageComponent,
    CultureLandingPageComponent,
    AiReadinessLandingPageComponent,
    AssessmentLandingPageComponent,
    SurveyxLandingComponent,
    TenantSetupComponent,
    EntityStructureDialogComponent,
    MainFrameComponent,
    AccountOverviewComponent,
    ChangePasswordComponent,
    ResetPasswordComponent,
    ChangeEmailComponent,
  ],
  imports: [
    SharedModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    BrowserModule,
    ThemeModule,
    AppRoutingModule,
    HammerModule,
    BidiModule,
    MatTreeModule,
    ExcelUploadModule,
    AbpProvidersModule,
    CoreKeyResolverModule,
    AnnouncementsModule,
  ],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA,
    NO_ERRORS_SCHEMA
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => getAuth()),
    provideDatabase(() => getDatabase()),
    provideStorage(() => getStorage()),
    provideUrls(),
    GlobalsService, LanguagePipe, {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true },
    },

    // { provide: ErrorHandler, useClass: CustomErrorHandlerService }
    { provide: HTTP_INTERCEPTORS, multi: true, useClass: GlobalErrorHandler, deps: [Injector] },
    // { provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true },
    ExtractPipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthTokenInterceptor,
      multi: true,
      deps: [TokenService]
    },
    {
      provide: KEY_RESOLVER_TOKEN,
      useClass: JobGradeResolver,
      multi: true
    },
    {
      provide: KEY_RESOLVER_TOKEN,
      useClass: EntityGroupResolver,
      multi: true
    },
    {
      provide: KEY_RESOLVER_TOKEN,
      useClass: FixedGroupingOptionsResolver,
      multi: true
    },
    {
      provide: KEY_RESOLVER_TOKEN,
      useClass: OnboardingExperienceScaleResolver,
      multi: true
    },
    {
      provide: KEY_RESOLVER_TOKEN,
      useClass: OnboardingPhaseResolver,
      multi: true
    },
    {
      provide: KEY_RESOLVER_TOKEN,
      useClass: SurveyStatusResolver,
      multi: true
    },
    {
      provide: KEY_RESOLVER_TOKEN,
      useClass: JobRoleResolver,
      multi: true
    },
    {
      provide: KEY_RESOLVER_TOKEN,
      useClass: ExtraCriteriaResolver,
      multi: true
    },
    {
      provide: KEY_RESOLVER_TOKEN,
      useClass: BandLevelResolver,
      multi: true
    },


    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: tooltipDefaults }

  ],
  bootstrap: [AppComponent]
})

export class AppModule {
  private snackbarOverlay: OverlayRef = this.overlay.create({ panelClass: 'z-999998' });
  constructor(private injector: Injector, private router: Router, private metaService: MetaService, private overlay: Overlay) {
    setAppInjector(this.injector);
    this.router.events

      .pipe(
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(event => {
        console.log(event);
        const title = this.metaService.getRouteTitle();
        this.metaService.setTitle(title);
      });

    if (!this.snackbarOverlay.hasAttached())
      this.snackbarOverlay.attach(new ComponentPortal(ZenSnackbarComponent));

    registerOpenAPI();
  }




}
